import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, Waves, FlaskConical, ChevronDown, ChevronUp, AlertTriangle, Lock, TrendingUp } from 'lucide-react';
import { cn } from '../ui/utils';
import { FeetInchesInput } from '../ui/FeetInchesInput';
import { 
    sanitizeNumericInput, 
    validateProductionValue,
    validateTankGauges,
    PRODUCTION_VALIDATION_RULES 
} from '../../utils/validation/productionValidation';
import { FieldSyncIndicator } from './FieldSyncIndicator';
import type { FieldSyncStatus } from '../../types/draftEntry';

// Kolor Kut reading data - measures water/oil separation in tank
export interface KolorKutReading {
    enabled: boolean;
    waterCutInches: string;  // Water level found by paste (in inches)
    notes: string;
}

// Reuse interface from DataEntry, but defined here for portability
export interface TankReading {
    tankId: string;
    tankNumber: number;
    type: 'oil' | 'water';
    topGauge: string;
    bottomGauge: string;
    netVolume: number;
    tankName: string;
    bblPerInch: number;
    capacity: number;
    // Kolor Kut fields
    kolorKut?: KolorKutReading;
}

interface TankLevelsProps {
    tankReadings: TankReading[];
    onGaugeChange: (tankId: string, field: 'topGauge' | 'bottomGauge', value: string) => void;
    onKolorKutChange: (tankId: string, kolorKut: KolorKutReading) => void;
    onRetrySync?: (tankId: string, field: string) => void;
    isDarkMode: boolean;
    validationErrors?: Map<string, string>;
    fieldSyncStatuses?: Map<string, FieldSyncStatus>;
    isLocked?: boolean;  // Entry is submitted and locked
}

export function TankLevels({ 
    tankReadings, 
    onGaugeChange, 
    onKolorKutChange, 
    onRetrySync,
    isDarkMode, 
    validationErrors = new Map(),
    fieldSyncStatuses = new Map(),
    isLocked = false 
}: TankLevelsProps) {
    const [expandedKolorKut, setExpandedKolorKut] = useState<Record<string, boolean>>({});
    const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

    if (tankReadings.length === 0) return null;

    const oilTanks = tankReadings.filter(t => t.type === 'oil');
    const waterTanks = tankReadings.filter(t => t.type === 'water');

    // Sanitize and validate gauge input
    const handleGaugeChange = (tankId: string, field: 'topGauge' | 'bottomGauge', value: string) => {
        if (isLocked) return; // Prevent changes if locked
        
        const sanitized = sanitizeNumericInput(value, true, false);
        onGaugeChange(tankId, field, sanitized);
        
        // Validate the value
        const result = validateProductionValue(sanitized, 'tankGauge');
        const errorKey = `${tankId}_${field}`;
        
        if (!result.isValid && result.error) {
            setLocalErrors(prev => ({ ...prev, [errorKey]: result.error! }));
        } else {
            setLocalErrors(prev => {
                const updated = { ...prev };
                delete updated[errorKey];
                return updated;
            });
        }
    };

    // Sanitize Kolor Kut input
    const handleKolorKutChange = (tankId: string, currentKolorKut: KolorKutReading | undefined, value: string) => {
        if (isLocked) return; // Prevent changes if locked
        
        const sanitized = sanitizeNumericInput(value, true, false);
        onKolorKutChange(tankId, {
            enabled: currentKolorKut?.enabled ?? true,
            waterCutInches: sanitized,
            notes: currentKolorKut?.notes || '',
        });
    };

    const getFieldError = (tankId: string, field: string) => {
        return localErrors[`${tankId}_${field}`] || validationErrors.get(`tank_${tankId}_${field}`);
    };
    
    const getFieldSyncStatus = (tankId: string, field: string): FieldSyncStatus => {
        return fieldSyncStatuses.get(`tank_${tankId}_${field}`) || 'pristine';
    };
    
    // Get color for tank gauge input based on sync status
    const getGaugeInputColor = (tankId: string, field: string, hasValue: boolean) => {
        const error = getFieldError(tankId, field);
        const syncStatus = getFieldSyncStatus(tankId, field);
        
        if (error) return 'border-red-500 ring-1 ring-red-500';
        if (!hasValue) return isDarkMode ? 'border-slate-700' : 'border-gray-200';
        
        // Sync-based coloring
        if (syncStatus === 'submitted') {
            return isDarkMode
                ? 'border-emerald-500 ring-1 ring-emerald-500/50 bg-emerald-900/30'
                : 'border-emerald-500 ring-1 ring-emerald-500/50 bg-emerald-50';
        }
        if (syncStatus === 'synced') {
            return isDarkMode
                ? 'border-blue-500/50 ring-1 ring-blue-500/50 bg-blue-900/20'
                : 'border-blue-500/50 ring-1 ring-blue-500/50 bg-blue-50';
        }
        if (syncStatus === 'local' || syncStatus === 'syncing') {
            return isDarkMode
                ? 'border-orange-500/50 ring-1 ring-orange-500/50 bg-orange-900/20'
                : 'border-orange-500/50 ring-1 ring-orange-500/50 bg-orange-50';
        }
        if (syncStatus === 'error') {
            return isDarkMode
                ? 'border-red-500/50 ring-1 ring-red-500/50 bg-red-900/20'
                : 'border-red-500/50 ring-1 ring-red-500/50 bg-red-50';
        }
        
        // Default filled state
        return isDarkMode
            ? 'border-emerald-500/50 bg-emerald-900/20'
            : 'border-emerald-500/50 bg-emerald-50';
    };

    // Calculate estimated tank height in inches from capacity and bblPerInch
    // Standard tanks are typically 15-20 feet tall (180-240 inches)
    const getEstimatedTankHeight = (capacity: number, bblPerInch: number): number => {
        if (bblPerInch <= 0) return 180; // Default ~15 feet
        return Math.round(capacity / bblPerInch);
    };

    // Check if gauge reading exceeds tank capacity
    const getTankCapacityWarning = (tank: TankReading): string | null => {
        const topGauge = parseFloat(tank.topGauge) || 0;
        if (topGauge <= 0) return null;
        
        const estimatedHeight = getEstimatedTankHeight(tank.capacity, tank.bblPerInch);
        
        if (topGauge > estimatedHeight) {
            return `Reading (${topGauge}") exceeds estimated tank height (~${estimatedHeight}")`;
        }
        if (topGauge > estimatedHeight * 0.95) {
            return `Tank nearly full (${Math.round((topGauge / estimatedHeight) * 100)}% capacity)`;
        }
        return null;
    };

    // Format feet and inches for display
    const formatGaugeReading = (totalInches: string): string => {
        const inches = parseFloat(totalInches) || 0;
        if (inches === 0) return '0"';
        const feet = Math.floor(inches / 12);
        const remainingInches = Math.round(inches % 12);
        if (feet === 0) return `${remainingInches}"`;
        if (remainingInches === 0) return `${feet}'`;
        return `${feet}' ${remainingInches}"`;
    };

    const toggleKolorKut = (tankId: string, currentKolorKut?: KolorKutReading) => {
        const newEnabled = !currentKolorKut?.enabled;
        onKolorKutChange(tankId, {
            enabled: newEnabled,
            waterCutInches: currentKolorKut?.waterCutInches || '',
            notes: currentKolorKut?.notes || '',
        });
        setExpandedKolorKut(prev => ({ ...prev, [tankId]: newEnabled }));
    };

    const updateKolorKutField = (tankId: string, currentKolorKut: KolorKutReading | undefined, field: keyof KolorKutReading, value: any) => {
        onKolorKutChange(tankId, {
            enabled: currentKolorKut?.enabled ?? true,
            waterCutInches: currentKolorKut?.waterCutInches || '',
            notes: currentKolorKut?.notes || '',
            [field]: value,
        });
    };

    const renderTankGroup = (tanks: TankReading[], title: string, icon: React.ReactNode, colorClass: string, showKolorKut: boolean = false) => (
        <div className="space-y-3">
            <h4 className={cn("text-sm font-semibold flex items-center gap-2", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                {icon}
                {title}
                {isLocked && <Lock className="w-3.5 h-3.5 text-slate-400" />}
            </h4>
            <div className="grid grid-cols-1 gap-3">
                {tanks.map((tank) => {
                    const isKolorKutExpanded = tank.kolorKut?.enabled || expandedKolorKut[tank.tankId];
                    const topHasValue = tank.topGauge !== '' && tank.topGauge !== '0';
                    const bottomHasValue = tank.bottomGauge !== '' && tank.bottomGauge !== '0';
                    const capacityWarning = getTankCapacityWarning(tank);
                    const estimatedHeight = getEstimatedTankHeight(tank.capacity, tank.bblPerInch);
                    const fillPercent = topHasValue ? Math.min(100, Math.round((parseFloat(tank.topGauge) / estimatedHeight) * 100)) : 0;
                    
                    return (
                        <motion.div
                            key={tank.tankId}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className={cn(
                                "p-4 rounded-2xl border transition-all",
                                isDarkMode
                                    ? "bg-slate-900 border-slate-800"
                                    : "bg-white border-gray-100 shadow-sm",
                                isLocked && "opacity-85",
                                capacityWarning && (isDarkMode ? "border-amber-700/50" : "border-amber-300")
                            )}
                        >
                            {/* Tank Header with Name and BBL Preview */}
                            <div className="flex justify-between items-center mb-3">
                                <div className="flex items-center gap-2">
                                    <span className={cn("text-xs font-bold uppercase tracking-wider", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                                        {tank.tankName}
                                    </span>
                                    {tank.capacity > 0 && (
                                        <span className={cn("text-[10px] px-1.5 py-0.5 rounded", isDarkMode ? "bg-slate-800 text-slate-500" : "bg-gray-100 text-gray-400")}>
                                            {tank.capacity} BBL tank
                                        </span>
                                    )}
                                </div>
                                {/* Real-time BBL Preview - Enhanced */}
                                <div className={cn(
                                    "flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-all",
                                    tank.netVolume > 0
                                        ? isDarkMode 
                                            ? "bg-emerald-900/40 border border-emerald-700/50" 
                                            : "bg-emerald-50 border border-emerald-200"
                                        : isDarkMode 
                                            ? "bg-slate-800" 
                                            : "bg-gray-100"
                                )}>
                                    {tank.netVolume > 0 && (
                                        <TrendingUp className={cn("w-3.5 h-3.5", isDarkMode ? "text-emerald-400" : "text-emerald-600")} />
                                    )}
                                    <span className={cn(
                                        "text-sm font-bold tabular-nums",
                                        tank.netVolume > 0
                                            ? isDarkMode ? "text-emerald-300" : "text-emerald-700"
                                            : isDarkMode ? "text-slate-400" : "text-gray-500"
                                    )}>
                                        {tank.netVolume.toFixed(1)} BBL
                                    </span>
                                </div>
                            </div>

                            {/* Calculation breakdown - shows formula when has value */}
                            {topHasValue && (
                                <div className={cn(
                                    "mb-3 px-3 py-2 rounded-lg text-xs",
                                    isDarkMode ? "bg-slate-800/50 text-slate-400" : "bg-slate-50 text-slate-500"
                                )}>
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono">
                                            {formatGaugeReading(tank.topGauge)}
                                            {bottomHasValue && parseFloat(tank.bottomGauge) > 0 && (
                                                <> − {formatGaugeReading(tank.bottomGauge)}</>
                                            )}
                                            {' × '}{tank.bblPerInch.toFixed(2)} BBL/in
                                        </span>
                                        {fillPercent > 0 && (
                                            <span className={cn(
                                                "font-medium",
                                                fillPercent >= 95 ? "text-amber-500" : fillPercent >= 80 ? "text-blue-500" : ""
                                            )}>
                                                ~{fillPercent}% full
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Capacity Warning */}
                            {capacityWarning && (
                                <div className={cn(
                                    "mb-3 px-3 py-2 rounded-lg flex items-center gap-2 text-xs",
                                    isDarkMode 
                                        ? "bg-amber-900/30 text-amber-300 border border-amber-700/50" 
                                        : "bg-amber-50 text-amber-700 border border-amber-200"
                                )}>
                                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span>{capacityWarning}</span>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className={cn("block text-[10px] uppercase", isDarkMode ? "text-slate-500" : "text-gray-400")}>
                                            Top Gauge
                                        </label>
                                        <FieldSyncIndicator
                                            status={getFieldSyncStatus(tank.tankId, 'topGauge')}
                                            onRetry={onRetrySync ? () => onRetrySync(tank.tankId, 'topGauge') : undefined}
                                            size="sm"
                                        />
                                    </div>
                                    <FeetInchesInput
                                        value={tank.topGauge}
                                        onChange={(value) => handleGaugeChange(tank.tankId, 'topGauge', value)}
                                        disabled={isLocked}
                                        readOnly={isLocked}
                                        isDarkMode={isDarkMode}
                                        error={getFieldError(tank.tankId, 'topGauge')}
                                        maxTotalInches={500}
                                        syncStatus={getFieldSyncStatus(tank.tankId, 'topGauge')}
                                    />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className={cn("block text-[10px] uppercase", isDarkMode ? "text-slate-500" : "text-gray-400")}>
                                            Bottom Gauge
                                        </label>
                                        <FieldSyncIndicator
                                            status={getFieldSyncStatus(tank.tankId, 'bottomGauge')}
                                            onRetry={onRetrySync ? () => onRetrySync(tank.tankId, 'bottomGauge') : undefined}
                                            size="sm"
                                        />
                                    </div>
                                    <FeetInchesInput
                                        value={tank.bottomGauge}
                                        onChange={(value) => handleGaugeChange(tank.tankId, 'bottomGauge', value)}
                                        disabled={isLocked}
                                        readOnly={isLocked}
                                        isDarkMode={isDarkMode}
                                        error={getFieldError(tank.tankId, 'bottomGauge')}
                                        maxTotalInches={500}
                                        syncStatus={getFieldSyncStatus(tank.tankId, 'bottomGauge')}
                                    />
                                </div>
                            </div>

                            {/* Kolor Kut Toggle - Only for Oil Tanks */}
                            {showKolorKut && (
                                <div className="mt-4">
                                    <button
                                        type="button"
                                        onClick={() => !isLocked && toggleKolorKut(tank.tankId, tank.kolorKut)}
                                        disabled={isLocked}
                                        className={cn(
                                            "w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
                                            tank.kolorKut?.enabled
                                                ? isDarkMode
                                                    ? "bg-amber-900/30 border border-amber-700/50 text-amber-300"
                                                    : "bg-amber-50 border border-amber-200 text-amber-700"
                                                : isDarkMode
                                                    ? "bg-slate-800 border border-slate-700 text-slate-400 hover:bg-slate-700 hover:text-slate-300"
                                                    : "bg-gray-50 border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-600"
                                        )}
                                    >
                                        <span className="flex items-center gap-2">
                                            <FlaskConical className={cn("w-4 h-4", tank.kolorKut?.enabled ? "text-amber-500" : "")} />
                                            Kolor Kut Reading
                                        </span>
                                        {isKolorKutExpanded ? (
                                            <ChevronUp className="w-4 h-4" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4" />
                                        )}
                                    </button>

                                    <AnimatePresence>
                                        {isKolorKutExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="overflow-hidden"
                                            >
                                                <div className={cn(
                                                    "mt-3 p-4 rounded-xl border space-y-4",
                                                    isDarkMode
                                                        ? "bg-amber-950/20 border-amber-900/30"
                                                        : "bg-amber-50/50 border-amber-100"
                                                )}>
                                                    {/* Water Level in Tank */}
                                                    <div>
                                                        <label className={cn("block text-[10px] uppercase mb-2 font-semibold", isDarkMode ? "text-amber-400/70" : "text-amber-600")}>
                                                            Water Level (inches) <span className="font-normal text-amber-600/50">max 500"</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            placeholder='0"'
                                                            value={tank.kolorKut?.waterCutInches || ''}
                                                            onChange={(e) => handleKolorKutChange(tank.tankId, tank.kolorKut, e.target.value)}
                                                            className={cn(
                                                                "w-full px-3 py-2.5 rounded-xl text-sm border focus:ring-2 transition-all",
                                                                isDarkMode
                                                                    ? "bg-slate-950 border-amber-800/50 focus:border-amber-500 focus:ring-amber-900 text-white placeholder-slate-500"
                                                                    : "bg-white border-amber-200 focus:border-amber-500 focus:ring-amber-100 text-slate-900 placeholder-gray-400"
                                                            )}
                                                        />
                                                        <p className={cn("text-[10px] mt-1.5", isDarkMode ? "text-slate-500" : "text-gray-400")}>
                                                            Water found at bottom of tank (oil floats above)
                                                        </p>
                                                    </div>

                                                    {/* Notes */}
                                                    <div>
                                                        <label className={cn("block text-[10px] uppercase mb-2 font-semibold", isDarkMode ? "text-amber-400/70" : "text-amber-600")}>
                                                            Notes (optional)
                                                        </label>
                                                        <textarea
                                                            placeholder="e.g., Clear color change at 4 inches, emulsion layer present..."
                                                            value={tank.kolorKut?.notes || ''}
                                                            onChange={(e) => updateKolorKutField(tank.tankId, tank.kolorKut, 'notes', e.target.value)}
                                                            rows={2}
                                                            className={cn(
                                                                "w-full px-3 py-2.5 rounded-xl text-sm border focus:ring-2 transition-all resize-none",
                                                                isDarkMode
                                                                    ? "bg-slate-950 border-amber-800/50 focus:border-amber-500 focus:ring-amber-900 text-white placeholder-slate-500"
                                                                    : "bg-white border-amber-200 focus:border-amber-500 focus:ring-amber-100 text-slate-900 placeholder-gray-400"
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {oilTanks.length > 0 && renderTankGroup(
                oilTanks,
                'Oil Tanks',
                <Droplets className="w-4 h-4 text-emerald-500" />,
                'text-emerald-500',
                true // Enable Kolor Kut for oil tanks
            )}
            {waterTanks.length > 0 && renderTankGroup(
                waterTanks,
                'Water Tanks',
                <Waves className="w-4 h-4 text-blue-500" />,
                'text-blue-500',
                false // No Kolor Kut for water tanks
            )}
        </div>
    );
}
